import * as L from 'leaflet';
import { TIMESLIDER_KNOB_PRESSED, TIMESLIDER_KNOB_RELEASED, RESET_TIMESTEPS, TIME_PLAYBACK_TICKED, } from './events';
import { findClosestIndex, getPositionFromIndex } from './lib/utils/array';
import { Slider } from './lib/Slider';
import { addClassNames } from './lib/utils/dom';
import { DateTime } from 'luxon';
/**
 * The timeslider control's default options.
 */
const CONTROL_TIMESLIDER_OPTIONS_DEFAULT = {
    name: 'timeslider',
    position: 'bottomright',
    format: (dt) => dt.toLocaleString(DateTime.DATETIME_FULL),
};
/**
 * The timeslider control.
 */
export class ControlTimeslider extends L.Control {
    /**
     * Create a new timeslider control instance.
     * @param options - The timeslider control options.
     * @param maapInstance - The M.A.A.P. instance.
     * @listens {@link RESET_TIMESTEPS}
     */
    constructor(options, maapInstance) {
        super(Object.assign(Object.assign({}, CONTROL_TIMESLIDER_OPTIONS_DEFAULT), options));
        this.maapInstance = maapInstance;
        this.slider = new Slider({
            // dummy initialization
            tickRecords: [],
            initialTick: 0,
            onStateChangedCb: (value) => {
                this.maapInstance.time.setTime(value);
            },
            onPointerDownCb: () => {
                this.maapInstance.fire(TIMESLIDER_KNOB_PRESSED);
            },
            onPointerUpCb: () => {
                this.maapInstance.fire(TIMESLIDER_KNOB_RELEASED);
            },
            options: {
                isTooltipPermanent: true,
            },
        });
        this.maapInstance.on(RESET_TIMESTEPS, () => {
            // reset slider ticks
            const timesteps = this.maapInstance.time.getTimesteps();
            const tickRecords = timesteps.map((dt) => {
                return {
                    label: this.options.format(dt),
                    value: dt,
                };
            });
            this.slider.updateTicks(tickRecords);
            // highlight past and future parts of visual slider track
            this.slider.resizeTracks(this.calculateTrackRightSize(timesteps));
            this.updatePositionFromTime();
        });
        this.maapInstance.on(TIME_PLAYBACK_TICKED, this.updatePositionFromTime, this);
    }
    /**
     * Calculate the right hand track size (in percentage relative to the
     * timeslider).
     * @param timesteps - The available timesteps.
     * @returns The right hand track size in percentage.
     */
    calculateTrackRightSize(timesteps) {
        const closestItemIndex = findClosestIndex(DateTime.utc(), timesteps, (a, b) => {
            return Math.abs(a.diff(b).toMillis());
        });
        return 100 - getPositionFromIndex(timesteps, closestItemIndex);
    }
    updatePositionFromTime() {
        const dtCurrent = this.maapInstance.time.currentTime();
        if (!dtCurrent)
            return;
        const tickIndex = this.slider
            .getTicks()
            .findIndex((tick) => tick.value.equals(dtCurrent));
        if (tickIndex === -1) {
            throw new Error(`Could not find tick for ${dtCurrent}.`);
        }
        this.slider.updateState({ selectedTick: tickIndex });
    }
    /**
     * The hook to execute when the control is added to the map.
     * @returns The container element to be added to the map.
     * @see {@link https://leafletjs.com/reference-1.7.1.html#control-extension-methods}
     */
    onAdd() {
        const elContainer = this.slider.getContainerElement();
        addClassNames(elContainer, ['maap_p-bottom-controls__control']);
        return elContainer;
    }
}
//# sourceMappingURL=ControlTimeslider.js.map